import { computed, inject, Injectable, signal } from '@angular/core';
import { ApiEventsService } from '../shared/services/api-events.service';
import { catchError, distinctUntilChanged, filter, map, of, startWith, switchMap, tap } from 'rxjs';
import { Languages, TranslationsService } from '@goatsports/shared/util';
import { GroupsService, NotificationService, RoleType, UsersService } from '@goatsports/core/data-access';
import { CurrentUserService } from '../shared/services/current-user.service';
import { MenuItemModel } from '@syncfusion/ej2-angular-navigations';
import { ProfileMenuItem } from '../shared/models/profile-menu-item';
import { ApplicationStateService } from '../shared/services/application-state.service';
import { NavigationEnd, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { ErrorService } from '../shared/services/error.service';
import { AuthenticationService } from '../shared/services/authentication.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

type State = {
  unreadMessagesCount: number;
  unreadNotificationsCount: number;
};

@Injectable()
export class ShellService {
  private readonly errorService = inject(ErrorService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly apiEventsService = inject(ApiEventsService);
  private readonly translationsService = inject(TranslationsService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly groupService = inject(GroupsService);
  private readonly notificationsService = inject(NotificationService);
  private readonly applicationStateService = inject(ApplicationStateService);
  private readonly usersService = inject(UsersService);
  private readonly router = inject(Router);
  private readonly titleService = inject(Title);

  private readonly INITIAL_STATE: State = {
    unreadMessagesCount: 0,
    unreadNotificationsCount: 0,
  };

  private readonly state = signal<State>(this.INITIAL_STATE);

  // selectors
  unreadMessagesCount = computed(() => this.state().unreadMessagesCount);
  unreadNotificationsCount = computed(() => this.state().unreadNotificationsCount);

  isErrorThrown = toSignal(this.errorService.isErrorThrown$);

  // sources

  menuItems = toSignal(
    this.applicationStateService.languageChanged$.pipe(
      map((language) => {
        return this.loadMenuItems(language);
      }),
    ),
    { initialValue: [] },
  );

  private unreadMessagesCount$ = this.fetchGroupsUnreadMessagesCount().pipe(
    switchMap((initialValue) =>
      this.apiEventsService.unreadMessagesCount$.pipe(
        map((res) => res.totalUnreadCount),
        startWith(initialValue),
      ),
    ),
  );

  private unreadNotificationsCount$ = this.fetchNotificationsUnreadCount().pipe(
    switchMap((initialValue) =>
      this.apiEventsService.unreadNotificationsCount$.pipe(
        map((res) => res.unreadCount),
        startWith(initialValue),
      ),
    ),
  );

  language = toSignal(this.applicationStateService.languageChanged$, {
    initialValue: 'en',
  });

  routerUrl = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      tap(() => {
        this.errorService.clearServerErrors();
      }),
      map((events) => ((events as any)?.urlAfterRedirects as string) ?? this.router.url),
    ),
    { initialValue: this.router.url },
  );

  constructor() {
    this.unreadMessagesCount$.pipe(takeUntilDestroyed()).subscribe((unreadMessagesCount) => {
      this.state.update((state) => ({
        ...state,
        unreadMessagesCount,
      }));
    });

    this.unreadNotificationsCount$.pipe(takeUntilDestroyed()).subscribe((unreadNotificationsCount) => {
      this.state.update((state) => ({
        ...state,
        unreadNotificationsCount,
      }));
    });
  }

  get currentUserRole() {
    return this.currentUserService.currentUserRole;
  }

  private get fallbackFirstName(): string {
    const fromJwt = this.currentUserService.currentUserFirstName;
    if (fromJwt) return fromJwt;
    // Read the raw JWT claims directly (the local `currentUserEmail` getter
    // String()-wraps the value so a missing claim becomes the literal
    // "undefined"). Some tokens carry `emailaddress` instead of `email`.
    const email = this.currentUserService.currentUserEmail ?? this.currentUserService.currentUserEmailAddress;
    if (!email) return '';
    return email.includes('@') ? email.split('@')[0] : email;
  }

  // Re-fetch the profile whenever the auth token changes user. `startWith(null)`
  // bootstraps the initial emission; `distinctUntilChanged` on the UID keeps
  // silent refreshes from re-querying the same user.
  //
  // Uses GET /Users/Me (slim self-only endpoint) rather than GET /Users/{uid} —
  // the latter enforces UserPermissionPolicy and 403s for a Coach reading their
  // own record, which the FE http interceptor turns into an auto-logout. The
  // JWT can't substitute here because given_name/family_name claims aren't
  // reliably populated, so the DB remains the source of truth for the header.
  private readonly currentUserProfile = toSignal(
    this.authenticationService.isTokenReceived().pipe(
      startWith(null),
      map(() => this.currentUserService.currentUserUid),
      distinctUntilChanged(),
      switchMap((uid) => {
        const fallback = {
          firstName: this.fallbackFirstName,
          lastName: this.currentUserService.currentUserLastName,
        };
        if (!uid) return of(fallback);
        return this.usersService.usersMeGet().pipe(
          map((res) => ({
            firstName: res.firstName?.trim() || fallback.firstName,
            lastName: res.lastName?.trim() || fallback.lastName,
          })),
          catchError(() => of(fallback)),
          startWith(fallback),
        );
      }),
    ),
    { requireSync: true },
  );

  readonly currentUserFullName = computed(() => {
    const { firstName, lastName } = this.currentUserProfile();
    return [firstName, lastName].filter(Boolean).join(' ');
  });

  // Avatar initials: first-name + last-name initials, matching the convention
  // used in chat-bubble.component.ts. Falls back to first-name only if the
  // user has no last name on file.
  readonly currentUserInitials = computed(() => {
    const { firstName, lastName } = this.currentUserProfile();
    const first = firstName?.charAt(0)?.toUpperCase() ?? '';
    const last = lastName?.charAt(0)?.toUpperCase() ?? '';
    return first + last;
  });

  private fetchGroupsUnreadMessagesCount() {
    return this.currentUserRole !== RoleType.Translator ? this.groupService.groupsUnreadCountGet() : of(0);
  }

  private fetchNotificationsUnreadCount() {
    return this.currentUserRole !== RoleType.Translator
      ? this.notificationsService.notificationUnreadCountGet().pipe(map((res) => res.unreadCount ?? 0))
      : of(0);
  }

  get currentUserEmail() {
    const emailString = String(this.currentUserService.currentUserEmail); // konvertuj u string
    const firstEmail = emailString.split(',')[0].trim(); // uzmi prvi email
    return firstEmail;
  }

  changeLanguage(text: string) {
    const selectedLanguage = Languages.availableLanguages.find((x) => x.label === text)?.id;

    if (!selectedLanguage) return;

    this.translationsService.changeLanguage(selectedLanguage);
  }

  private loadMenuItems(languageId: string) {
    const items: MenuItemModel[] = [
      {
        text: ProfileMenuItem.Icon,
        items: [
          { text: ProfileMenuItem.Profile },
          { text: ProfileMenuItem.ChangePassword },
          {
            text: ProfileMenuItem.Language,
            items: Languages.availableLanguages.map((x) => ({
              text: x.label,
              id: x.id === languageId ? ProfileMenuItem.Language : '',
            })),
          },
          { text: ProfileMenuItem.Logout },
        ],
      },
    ];
    return items;
  }

  logout() {
    this.authenticationService.logout();
  }
}
